export const MOBILE_AUTH_KEYBOARD_SHIFT_Y = -96;
export const MOBILE_AUTH_MAX_KEYBOARD_SHIFT_Y = -280;

const MOBILE_AUTH_KEYBOARD_BOTTOM_GAP = 8;
const MOBILE_AUTH_MIN_VISIBLE_HEIGHT = 0;
export const AUTH_CODE_KEYBOARD_CLEARANCE = 64;

export function getStableAuthKeyboardHeight({
  previousHeight = 0,
  keyboardHeight = 0,
  wasOpen = false,
  keepLayoutStable = false,
} = {}) {
  const height = Math.max(0, Number(keyboardHeight) || 0);
  // Login's username and password keyboards can have different QuickType
  // heights. Keep the smaller usable area throughout one keyboard session so
  // switching fields does not reflow the form and then scroll it back again.
  return wasOpen && keepLayoutStable
    ? Math.max(Math.max(0, Number(previousHeight) || 0), height)
    : height;
}

export function getRegistrationFieldScrollTop({
  scrollTop = 0,
  viewportTop = 0,
  viewportHeight = 0,
  fieldTop = 0,
  fieldHeight = 0,
  scrollHeight = 0,
  firstFieldTop = fieldTop,
  firstFieldHeight = fieldHeight,
  actionsBottom = fieldTop + fieldHeight,
  fieldIndex = 0,
  fieldCount = 1,
  padding = 20,
} = {}) {
  const metrics = { viewportTop, viewportHeight, scrollHeight, padding };
  // Use content coordinates so repeated measurements during smooth scrolling
  // request the same destination, rather than accumulating another shift.
  const firstScrollTop = getAuthFieldScrollTop({
    ...metrics,
    fieldTop: firstFieldTop + scrollTop,
    fieldHeight: firstFieldHeight,
  });
  const finalScrollTop = getAuthFieldScrollTop({
    ...metrics,
    fieldTop: viewportTop + padding,
    fieldHeight: Math.max(0, actionsBottom + scrollTop - viewportTop - padding),
  });
  const progress = fieldCount > 1
    ? Math.min(1, Math.max(0, fieldIndex / (fieldCount - 1)))
    : 1;
  const plannedScrollTop = firstScrollTop +
    Math.max(0, finalScrollTop - firstScrollTop) * progress;

  // The focused field wins on very small screens or with taller legal copy.
  return getAuthFieldScrollTop({
    ...metrics,
    scrollTop: plannedScrollTop,
    fieldTop: fieldTop + scrollTop - plannedScrollTop,
    fieldHeight,
  });
}

export function getNativeAuthViewportHeight({
  stageHeight = 0,
  keyboardHeight = 0,
  keyboardBottomInset = 0,
  keyboardOpen = false,
  minVisibleHeight = MOBILE_AUTH_MIN_VISIBLE_HEIGHT,
} = {}) {
  const restingHeight = Math.max(0, Number(stageHeight) || 0);
  if (!keyboardOpen || restingHeight <= 0 || keyboardHeight <= 0) {
    return restingHeight;
  }

  return Math.max(
    Math.max(0, Number(minVisibleHeight) || 0),
    restingHeight +
      Math.max(0, Number(keyboardBottomInset) || 0) -
      Math.max(0, Number(keyboardHeight) || 0)
  );
}

export function getAuthFieldScrollTop({
  scrollTop = 0,
  viewportTop = 0,
  viewportHeight = 0,
  fieldTop = 0,
  fieldHeight = 0,
  scrollHeight = 0,
  padding = 16,
} = {}) {
  const normalizedViewportHeight = Math.max(0, Number(viewportHeight) || 0);
  const normalizedFieldHeight = Math.max(0, Number(fieldHeight) || 0);
  const normalizedPadding = Math.max(0, Number(padding) || 0);
  const maxScrollTop = Math.max(
    0,
    (Number(scrollHeight) || 0) - normalizedViewportHeight
  );
  const normalizedScrollTop = Math.max(0, Number(scrollTop) || 0);
  const normalizedViewportTop = Number(viewportTop) || 0;
  const normalizedFieldTop = Number(fieldTop) || 0;
  const safeTop = normalizedViewportTop + normalizedPadding;
  const safeBottom =
    normalizedViewportTop + normalizedViewportHeight - normalizedPadding;
  const fieldBottom = normalizedFieldTop + normalizedFieldHeight;

  let requestedScrollTop = normalizedScrollTop;
  if (normalizedFieldTop < safeTop) {
    requestedScrollTop -= safeTop - normalizedFieldTop;
  } else if (fieldBottom > safeBottom) {
    requestedScrollTop += fieldBottom - safeBottom;
  }

  return Math.min(maxScrollTop, Math.max(0, requestedScrollTop));
}

export function getStableMobileAuthStageMetrics({
  previousWidth = 0,
  previousHeight = 0,
  previousViewportHeight = 0,
  previousBottomInset = 0,
  measuredWidth = 0,
  measuredHeight = 0,
  measuredViewportHeight = 0,
  measuredBottomInset = 0,
  isDesktop = false,
  isKeyboardActive = false,
} = {}) {
  const hasRestingBaseline =
    previousWidth > 0 && previousHeight > 0 && previousViewportHeight > 0;

  if (!isDesktop && isKeyboardActive && hasRestingBaseline) {
    return {
      width: previousWidth,
      height: previousHeight,
      viewportHeight: previousViewportHeight,
      bottomInset: previousBottomInset,
    };
  }

  return {
    width: measuredWidth,
    height: measuredHeight,
    viewportHeight: measuredViewportHeight,
    bottomInset: measuredBottomInset,
  };
}

export function getMobileAuthKeyboardShiftY({
  isDesktop = false,
  isKeyboardOpen = false,
  isInputFocused = false,
  stageHeight = 0,
  keyboardHeight = 0,
  keyboardBottomInset = 0,
  scale = 1,
  contentBottom = 0,
  allowFallback = true,
} = {}) {
  if (isDesktop || (!isKeyboardOpen && !isInputFocused)) return 0;

  const safeScale = Math.max(scale, 0.01);
  const hasKeyboardMetrics = stageHeight > 0 && keyboardHeight > 0;
  if (!hasKeyboardMetrics) {
    return allowFallback ? MOBILE_AUTH_KEYBOARD_SHIFT_Y : 0;
  }

  // The stage excludes both iOS safe areas, while Capacitor's keyboard height
  // already includes the home-indicator area. Put the bottom inset back before
  // subtracting the keyboard or the form is lifted roughly 34px too far.
  const visibleStageHeight =
    Math.max(0, stageHeight + keyboardBottomInset - keyboardHeight) / safeScale;
  const requiredShift =
    visibleStageHeight > 0 && contentBottom > 0
      ? contentBottom + MOBILE_AUTH_KEYBOARD_BOTTOM_GAP - visibleStageHeight
      : 0;
  const shiftMagnitude = Math.min(
    Math.abs(MOBILE_AUTH_MAX_KEYBOARD_SHIFT_Y),
    Math.max(0, requiredShift)
  );

  return shiftMagnitude > 0 ? -shiftMagnitude : 0;
}
